import { GetStaticProps, InferGetStaticPropsType } from "next";
import Link from "next/link";

import { AppShell } from "@chef/feature-app-shell";
import {
  ContentComponent,
  ISection,
  SanityQuery,
  getSectionData,
  useSectionData,
} from "@chef/feature-sanity";
import {
  useBillingQuery,
  useUpdateBillingAgreementAddonsMutation,
} from "@chef/state-management";
import { ADDON_SUBSCRIPTIONS, BRAND_PATHS } from "@chef/constants";
import { isEqualStrings } from "@chef/utils/equal";
import { Button, Snackbar } from "@chef/components";

export const RaatassPage = ({
  topSection: _topSection,
  bottomSection: _bottomSection,
}: InferGetStaticPropsType<typeof raatassGetStaticProps>) => {
  const [topSection] = useSectionData(_topSection);
  const [bottomSection] = useSectionData(_bottomSection);

  const { data: billingQuery, isFetching: isFetchingBilling } =
    useBillingQuery();

  const [updateBillingAddons, { isLoading: isMutating }] =
    useUpdateBillingAgreementAddonsMutation();

  const isLoading = isFetchingBilling || isMutating;

  const raatass = billingQuery?.billing.addonSubscriptions?.find(
    (s) => s.subscriptionId === ADDON_SUBSCRIPTIONS.RAATASS,
  );

  const handleOptIn = async () => {
    if (!billingQuery) {
      return;
    }

    // Check if the user already has a Råtass subscription
    // If so, make sure it's active
    // If not, add it
    const updatedAddonSubscriptions =
      billingQuery.billing.addonSubscriptions.map((sub) => {
        if (isEqualStrings(sub.subscriptionId, ADDON_SUBSCRIPTIONS.RAATASS)) {
          return {
            addonSubscriptionId: sub.subscriptionId,
            active: true,
          };
        }

        return { addonSubscriptionId: sub.subscriptionId, active: sub.active };
      });

    if (!raatass) {
      updatedAddonSubscriptions.push({
        addonSubscriptionId: ADDON_SUBSCRIPTIONS.RAATASS,
        active: true,
      });
    }

    await updateBillingAddons(updatedAddonSubscriptions);
  };

  return (
    <AppShell title="Råtass">
      <AppShell.Meta />
      <AppShell.Header />

      <AppShell.Main grid>
        {topSection && (
          <div className="col-span-full">
            <ContentComponent block={topSection.content} />
          </div>
        )}

        <div className="col-span-4 xl:col-start-5 md:col-start-3">
          {raatass?.active ? (
            <Snackbar success showIcon={false}>
              <div className="flex flex-col gap-4">
                <p>
                  <strong>Påmeldt Råtass</strong>
                </p>
                <p>
                  Du bidrar til idretten gjennom vårt samarbeid med Råtass.
                  Dermed går kr 10,- av hver bestilling til ditt idrettslag.
                </p>
                <p className="text-sm">
                  Administrer ditt Råtass-bidrag på{" "}
                  <Link
                    href={BRAND_PATHS.MY_SUBSCRIPTION_LINK}
                    className="underline"
                  >
                    mitt abonnement
                  </Link>
                  .
                </p>
              </div>
            </Snackbar>
          ) : (
            <Button onClick={handleOptIn} loading={isLoading} primary full>
              Klikk her for å komme i gang
            </Button>
          )}
        </div>

        {bottomSection && (
          <div className="col-span-full">
            <ContentComponent block={bottomSection.content} />
          </div>
        )}
      </AppShell.Main>

      <AppShell.Footer minified />
      <AppShell.TabBar />
    </AppShell>
  );
};

export const raatassGetStaticProps: GetStaticProps<
  {
    draftMode: boolean;
    topSection: ISection;
    bottomSection: ISection;
  },
  SanityQuery
> = async ({ draftMode = false }) => {
  const topSection = await getSectionData("raatass_top", {
    preview: draftMode,
  });

  const bottomSection = await getSectionData("raatass_bottom", {
    preview: draftMode,
  });

  if (
    (!topSection || !topSection.content) &&
    (!bottomSection || !bottomSection.content)
  ) {
    return {
      notFound: true,
      revalidate: 600,
    };
  }

  return {
    props: {
      draftMode,
      topSection,
      bottomSection,
    },
    revalidate: 600,
  };
};
